import React, { useEffect, useState, useMemo } from 'react';
import { collection, query, where, orderBy, getDocs, doc, Timestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useHeader } from '../../contexts/HeaderContext';
import CourseCard from './CourseCard';
import { useProgramGroupedCourses } from '../../hooks/useProgramGroupedCourses';

interface Course {
    id: string;
    name: string;   
    nameEn?: string;
    avatar?: string;
    scheduleInfo: string;
    status: 'active' | 'pending' | 'completed' | 'cancelled';
    location?: string;
    description?: string;
    hasTodayClass?: boolean;
}

const AllTeacherCourses: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [courses, setCourses] = useState<Course[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<string>('all'); // 项目状态筛选
    const [user, setUser] = useState<User | null>(null);
    const { setHeaderTheme } = useHeader();
    
    // 使用 useMemo 缓存日期对象，避免每次渲染都创建新的对象
    const { today, tomorrow } = useMemo(() => {
        // 获取今天的日期（0时0分0秒）
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // 获取明天的日期（0时0分0秒）
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        
        return { today, tomorrow };
    }, []);

    // 监听认证状态变化
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            if (currentUser) {
                console.log('用户已认证:', currentUser.uid);
            } else {
                console.log('用户未认证');
            }
        });

        return () => unsubscribe();
    }, []);

    useEffect(() => {
        // 设置透明背景和白色文字
        setHeaderTheme({
            bgColor: 'bg-transparent',
            textColor: 'text-white'
        });

        return () => {
            // 恢复默认样式
            setHeaderTheme({
                bgColor: 'bg-white',
                textColor: 'text-[#142b89]'
            });
        };
    }, [setHeaderTheme]);

    useEffect(() => {
        const fetchTeacherCourses = async () => {
            console.log('fetchTeacherCourses', user);
            if (!user) return;

            try {
                setLoading(true);
                setError(null);

                // 创建教师引用
                const teacherRef = doc(db, 'users', user.uid);
                
                // 查询教师负责的所有课程 - 使用 array-contains 查询，因为 teacher 是一个数组
                const coursesQuery = query(
                    collection(db, 'courses'),
                    where('teacher', 'array-contains', teacherRef),
                    orderBy('createdAt', 'desc')
                );
                
                const querySnapshot = await getDocs(coursesQuery);
                if (querySnapshot.empty) {
                    setCourses([]);
                    setLoading(false);
                    return;
                }
                
                let coursesData: Course[] = querySnapshot.docs.map(docSnapshot => {
                    const data = docSnapshot.data();
                    
                    return {
                        id: docSnapshot.id,
                        name: data.name || '',
                        nameEn: data.nameEn || '',
                        avatar: data.avatar || null,
                        scheduleInfo: data.scheduleInfo || '',
                        status: data.status || 'pending',
                        location: data.location || '',
                        description: data.description || '',
                        hasTodayClass: false // 初始值设为false
                    };
                });
                
                // 如果需要识别今日课程，可以在这里设置hasTodayClass
                // 但由于scheduleInfo是字符串，这里可能需要其他方式来判断
                
                // 按状态和今日课程排序
                coursesData.sort((a, b) => {
                    // 首先按照今日课程排序
                    if (a.hasTodayClass && !b.hasTodayClass) return -1;
                    if (!a.hasTodayClass && b.hasTodayClass) return 1;
                    
                    // 然后按照状态排序
                    const statusOrder: Record<string, number> = {
                        'active': 0,
                        'pending': 1,
                        'completed': 2,
                        'cancelled': 3
                    };
                    
                    return statusOrder[a.status] - statusOrder[b.status];
                });
                
                setCourses(coursesData);
            } catch (err) {
                console.error('获取教师课程失败:', err);
                setError(t('profile.coursesFetchError'));
            } finally {
                setLoading(false);
            }
        };

        fetchTeacherCourses();
    }, [user, t, today, tomorrow]);

    // 将课程按项目分组，并根据选中的项目状态过滤
    const groups = useProgramGroupedCourses(courses, statusFilter === 'all' ? undefined : statusFilter);
    
    
    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-800 to-blue-600 pt-20">
            {/* 顶部背景 */}
            <div className="relative w-full overflow-hidden">
                <div className="absolute inset-0 bg-[url('/images/background.jpg')] bg-cover bg-center opacity-10"></div>
                
                {/* 内容层 */}
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 z-10">
                    <div className="flex items-center mb-6">
                        <Link to="/profile" className="text-blue-200 hover:text-white mr-4">
                            <ArrowLeftIcon className="h-5 w-5" />
                        </Link>
                        <motion.h1 
                            className="text-3xl font-bold text-white"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            {t('teacher.allCourses')}
                        </motion.h1>
                    </div>
                    
                    {/* 筛选器 */}
                    <motion.div 
                        className="mb-8 flex flex-wrap items-center gap-2"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                    >
                        <div className="flex items-center text-blue-200 mr-2">
                            <FunnelIcon className="h-4 w-4 mr-1" />
                            <span>{t('teacher.filterByStatus')}:</span>
                        </div>
                        
                        <button
                            onClick={() => setStatusFilter('all')}
                            className={`px-3 py-1.5 rounded-full text-sm ${
                                statusFilter === 'all'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-blue-900/50 text-blue-200 hover:bg-blue-800/70'
                            }`}
                        >
                            {t('teacher.allStatuses')}
                        </button>
                        
                        <button
                            onClick={() => setStatusFilter('active')}
                            className={`px-3 py-1.5 rounded-full text-sm ${
                                statusFilter === 'active'
                                    ? 'bg-green-600 text-white'
                                    : 'bg-blue-900/50 text-blue-200 hover:bg-blue-800/70'
                            }`}
                        >
                            {t('program.statuses.active')}
                        </button>
                        
                        <button
                            onClick={() => setStatusFilter('upcoming')}
                            className={`px-3 py-1.5 rounded-full text-sm ${
                                statusFilter === 'upcoming'
                                    ? 'bg-yellow-600 text-white'
                                    : 'bg-blue-900/50 text-blue-200 hover:bg-blue-800/70'
                            }`}
                        >
                            {t('program.statuses.upcoming')}
                        </button>
                        
                        <button
                            onClick={() => setStatusFilter('draft')}
                            className={`px-3 py-1.5 rounded-full text-sm ${
                                statusFilter === 'draft'
                                    ? 'bg-gray-600 text-white'
                                    : 'bg-blue-900/50 text-blue-200 hover:bg-blue-800/70'
                            }`}
                        >
                            {t('program.statuses.draft')}
                        </button>
                        
                        <button
                            onClick={() => setStatusFilter('inactive')}
                            className={`px-3 py-1.5 rounded-full text-sm ${
                                statusFilter === 'inactive'
                                    ? 'bg-red-600 text-white'
                                    : 'bg-blue-900/50 text-blue-200 hover:bg-blue-800/70'
                            }`}
                        >
                            {t('program.statuses.inactive')}
                        </button>
                        
                        {statusFilter !== 'all' && (
                            <button
                                onClick={() => setStatusFilter('all')}
                                className="ml-2 p-1 rounded-full bg-blue-800/50 text-blue-200 hover:bg-blue-700/70"
                                title={t('common.clear')}
                            >
                                <XMarkIcon className="h-4 w-4" />
                            </button>
                        )}
                    </motion.div>
                </div>
            </div>
            
            {/* 主要内容区域 */}
            <div className="relative pb-20 z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
                    {loading ? (
                        <div className="p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                            <div className="flex justify-center items-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-200 border-t-transparent"></div>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                            <div className="text-center py-8">
                                <p className="text-red-300">{error}</p>
                            </div>
                        </div>
                    ) : groups.length === 0 ? (
                        <div className="p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                            <div className="text-center py-8">
                                <p className="text-blue-100">
                                    {statusFilter === 'all' 
                                        ? t('teacher.noCoursesWithStatus', { status: t('teacher.allStatuses') })
                                        : t('teacher.noCoursesWithStatus', { status: t(`program.statuses.${statusFilter}`) })}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-10">
                            {groups.map(group => (
                                <div key={group.programId || 'ungrouped'}>
                                    <h3 className="text-lg font-semibold text-blue-100 mb-4 flex items-center">
                                        {group.title}
                                        {group.subtitle && <span className="ml-2 text-sm font-normal text-blue-300">({group.subtitle})</span>}
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {group.courses.map((course, index) => (
                                            <CourseCard 
                                                key={`${group.programId}_${course.id}`}
                                                course={course}
                                                index={index}
                                                showStatusBadge={true}
                                                programId={group.programId || undefined}
                                                programName={group.title}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AllTeacherCourses; 